import { yupResolver } from '@hookform/resolvers/yup';
import * as Placeholder from '-/Custom/Placeholder';
import { Stack, useRouter } from 'expo-router';
import { FormProvider, useForm } from 'react-hook-form';
import { useClient } from 'urql';
import ActionButton from '@/components/UI/ActionButton';
import {
  BadgeAssigneeEditContent,
  GetBadgeAssigneeCreateForm,
} from '@/screens/BadgeAssigneeEdit';
import {
  type BadgeAssigneeEditFormValues,
  badgeAssigneeEditValidator,
  getBadgeAssigneeEditFormValues,
  NO_BADGE_ASSIGNEE_ID,
} from '@/screens/BadgeAssigneeEdit/BadgeAssigneeEdit.form';
import useMemberAssignmentStore, {
  getMemberAssignmentSelectedParam,
  hasMemberAssignmentContext,
} from '@/stores/useMemberAssignmentStore';

export default function MemberActionAssignBadgeDetails() {
  const { back, dismiss } = useRouter();
  const activeAssignment = useMemberAssignmentStore(
    (state) => state.activeAssignment,
  );
  const badgeId = activeAssignment?.badgeId ?? '';
  const organizationId = activeAssignment?.organizationId ?? undefined;
  const personId = activeAssignment?.personId ?? undefined;
  const selected = getMemberAssignmentSelectedParam(activeAssignment);
  const teamId = activeAssignment?.teamId ?? undefined;

  if (!badgeId || !hasMemberAssignmentContext(activeAssignment)) {
    return (
      <>
        <Stack.Screen options={{ title: 'Assign Badge' }} />
        <Placeholder.Container>
          <Placeholder.Icon name="ribbon-outline" />
          <Placeholder.Text>
            {hasMemberAssignmentContext(activeAssignment)
              ? 'Choose a badge to continue.'
              : 'Select members to assign a badge.'}
          </Placeholder.Text>
          <ActionButton
            forceLabel
            icon="arrow-back-outline"
            label="Choose Badge"
            variant="secondary"
            onPress={back}
          />
        </Placeholder.Container>
      </>
    );
  }

  return (
    <MemberActionAssignBadgeDetailsForm
      key={badgeId}
      badgeId={badgeId}
      eventId={organizationId}
      personId={personId}
      selected={selected}
      teamId={teamId}
      onSaved={dismiss}
    />
  );
}

function MemberActionAssignBadgeDetailsForm({
  badgeId,
  eventId,
  onSaved,
  personId,
  selected,
  teamId,
}: {
  badgeId: string;
  eventId?: string;
  onSaved: () => void;
  personId?: string;
  selected?: string;
  teamId?: string;
}) {
  const client = useClient();
  const form = useForm<BadgeAssigneeEditFormValues>({
    mode: 'onChange',
    resolver: yupResolver(badgeAssigneeEditValidator),
    defaultValues: async () => {
      const { data } = await client.query(
        GetBadgeAssigneeCreateForm,
        {
          badgeId,
          emptyAssigneeId: NO_BADGE_ASSIGNEE_ID,
        },
        { requestPolicy: 'network-only' },
      );
      const badge = data?.badge_by_pk;

      return getBadgeAssigneeEditFormValues({
        badgeAttributes: badge?.badge_attributes ?? [],
        badgeId: badge?.id ?? badgeId,
      });
    },
  });

  return (
    <FormProvider {...form}>
      <BadgeAssigneeEditContent
        badgeId={badgeId}
        eventId={eventId}
        personId={personId}
        selected={selected}
        showHeaderLeft={false}
        teamId={teamId}
        onSaved={onSaved}
      />
    </FormProvider>
  );
}
